import React, { useState, useEffect } from 'react';
import './Dienstuebergabe.css';
import './components.css';
import { useUser } from '../UserContext';
import * as XLSX from 'xlsx-js-style';
import {
  autoReconnectDirectory,
  hasSelectedDirectory,
  selectDirectory,
  saveFileToDirectory,
  listFilesInDirectory,
  loadFileFromDirectory
} from '../utils/fileSystem';

const CATEGORY = 'Dienstuebergabe';

function Dienstuebergabe() {
  const { user: currentUser } = useUser();
  const [activeTab, setActiveTab] = useState('kurzfristig');
  const [uebergaben, setUebergaben] = useState({
    kurzfristig: [],
    mittelfristig: [],
    langfristig: []
  });
  const [newUebergabe, setNewUebergabe] = useState({
    titel: '',
    beschreibung: '',
    kategorie: 'kurzfristig',
    prioritaet: 'normal'
  });
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isDirectorySelected, setIsDirectorySelected] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [nextId, setNextId] = useState(1);

  // Lade Übergaben beim Komponenten-Mount
  useEffect(() => {
    initializeComponent();
  }, []);

  const initializeComponent = async () => {
    setLoading(true);
    try {
      // Prüfen, ob bereits ein Verzeichnis ausgewählt wurde
      const hasDir = await hasSelectedDirectory(CATEGORY);
      setIsDirectorySelected(hasDir);
      
      if (hasDir) {
        // Versuche automatische Wiederverbindung
        const reconnected = await autoReconnectDirectory(CATEGORY);
        if (reconnected) {
          await loadUebergaben();
        } else {
          setMessage({ 
            text: 'Verbindung zum Speicherort verloren. Bitte wählen Sie erneut einen Ordner aus.', 
            type: 'warning' 
          });
        }
      }
    } catch (error) {
      console.error('Fehler bei der Initialisierung:', error);
      setMessage({ 
        text: 'Fehler bei der Initialisierung. Bitte wählen Sie einen Speicherort aus.', 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const loadUebergaben = async () => {
    setLoading(true);
    setMessage({ text: 'Lade Übergaben...', type: 'info' });
    
    try {
      // Lade die neueste Excel-Datei
      const files = await listFilesInDirectory(CATEGORY, 'Dienstübergabe', '.xlsx');
      
      if (files.length === 0) {
        // Keine Datei vorhanden, leere Struktur erstellen
        setUebergaben({ kurzfristig: [], mittelfristig: [], langfristig: [] });
        setNextId(1);
        setMessage({ text: 'Keine Übergaben gefunden. Erstellen Sie die erste Übergabe.', type: 'info' });
        return;
      }

      // Lade die neueste Datei
      const latestFile = files[0];
      const file = await loadFileFromDirectory(CATEGORY, latestFile.name);
      
      if (!file) {
        throw new Error('Datei konnte nicht geladen werden');
      }

      // Excel-Datei parsen
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Daten in die richtige Struktur umwandeln
      const neueUebergaben = { kurzfristig: [], mittelfristig: [], langfristig: [] };
      let maxId = 0;

      jsonData.forEach((row, index) => {
        const uebergabe = {
          id: row.ID || (index + 1),
          titel: row.Titel || '',
          beschreibung: row.Beschreibung || '',
          kategorie: row.Kategorie || 'kurzfristig',
          prioritaet: row.Priorität || 'normal',
          status: row.Status || 'offen',
          erstelltVon: row['Erstellt von'] || '',
          erstelltAm: row['Erstellt am'] ? new Date(row['Erstellt am']) : new Date(),
          bearbeitetVon: row['Bearbeitet von'] || '',
          bearbeitetAm: row['Bearbeitet am'] ? new Date(row['Bearbeitet am']) : null
        };

        neueUebergaben[uebergabe.kategorie].push(uebergabe);
        maxId = Math.max(maxId, uebergabe.id);
      });

      setUebergaben(neueUebergaben);
      setNextId(maxId + 1);
      setMessage({ text: `${jsonData.length} Übergaben geladen.`, type: 'success' });
      
    } catch (error) {
      console.error('Fehler beim Laden der Übergaben:', error);
      setMessage({ 
        text: `Fehler beim Laden: ${error.message}`, 
        type: 'error' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newUebergabe.titel.trim() || !newUebergabe.beschreibung.trim()) {
      setMessage({ text: 'Bitte füllen Sie alle Felder aus.', type: 'error' });
      return;
    }

    if (!isDirectorySelected) {
      setMessage({ text: 'Bitte wählen Sie zuerst einen Speicherort aus.', type: 'error' });
      return;
    }

    try {
      // Neue Übergabe erstellen
      const neueUebergabe = {
        id: nextId,
        titel: newUebergabe.titel,
        beschreibung: newUebergabe.beschreibung,
        kategorie: newUebergabe.kategorie,
        prioritaet: newUebergabe.prioritaet,
        status: 'offen',
        erstelltVon: currentUser.email,
        erstelltAm: new Date(),
        bearbeitetVon: currentUser.email,
        bearbeitetAm: new Date()
      };

      // Übergabe zur entsprechenden Kategorie hinzufügen
      setUebergaben(prev => ({
        ...prev,
        [neueUebergabe.kategorie]: [...prev[neueUebergabe.kategorie], neueUebergabe]
      }));

      // ID für nächste Übergabe erhöhen
      setNextId(prev => prev + 1);

      // Formular zurücksetzen
      setNewUebergabe({
        titel: '',
        beschreibung: '',
        kategorie: activeTab,
        prioritaet: 'normal'
      });
      setShowForm(false);

      // Automatisch speichern
      await saveUebergaben();
      
      setMessage({ text: 'Übergabe erfolgreich erstellt und gespeichert!', type: 'success' });
    } catch (error) {
      console.error('Fehler beim Erstellen der Übergabe:', error);
      setMessage({ text: `Fehler beim Erstellen: ${error.message}`, type: 'error' });
    }
  };

  const handleStatusChange = async (id, neuerStatus) => {
    try {
      // Status in allen Kategorien aktualisieren
      setUebergaben(prev => {
        const updated = { ...prev };
        Object.keys(updated).forEach(kategorie => {
          updated[kategorie] = updated[kategorie].map(uebergabe => 
            uebergabe.id === id 
              ? { 
                  ...uebergabe, 
                  status: neuerStatus,
                  bearbeitetVon: currentUser.email,
                  bearbeitetAm: new Date()
                }
              : uebergabe
          );
        });
        return updated;
      });

      // Automatisch speichern
      await saveUebergaben();
      
      setMessage({ text: 'Status erfolgreich aktualisiert!', type: 'success' });
    } catch (error) {
      console.error('Fehler beim Aktualisieren des Status:', error);
      setMessage({ text: `Fehler beim Aktualisieren: ${error.message}`, type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Möchten Sie diese Übergabe wirklich löschen?')) {
      try {
        // Übergabe aus allen Kategorien entfernen
        setUebergaben(prev => {
          const updated = { ...prev };
          Object.keys(updated).forEach(kategorie => {
            updated[kategorie] = updated[kategorie].filter(uebergabe => uebergabe.id !== id);
          });
          return updated;
        });

        // Automatisch speichern
        await saveUebergaben();
        
        setMessage({ text: 'Übergabe erfolgreich gelöscht!', type: 'success' });
      } catch (error) {
        console.error('Fehler beim Löschen der Übergabe:', error);
        setMessage({ text: `Fehler beim Löschen: ${error.message}`, type: 'error' });
      }
    }
  };

  const saveUebergaben = async () => {
    try {
      // Alle Übergaben in ein Array sammeln
      const alleUebergaben = [
        ...uebergaben.kurzfristig,
        ...uebergaben.mittelfristig,
        ...uebergaben.langfristig
      ];

      // Daten für Excel vorbereiten
      const excelData = alleUebergaben.map(uebergabe => ({
        ID: uebergabe.id,
        Titel: uebergabe.titel,
        Beschreibung: uebergabe.beschreibung,
        Kategorie: uebergabe.kategorie,
        Priorität: uebergabe.prioritaet,
        Status: uebergabe.status,
        'Erstellt von': uebergabe.erstelltVon,
        'Erstellt am': uebergabe.erstelltAm,
        'Bearbeitet von': uebergabe.bearbeitetVon,
        'Bearbeitet am': uebergabe.bearbeitetAm
      }));

      // Arbeitsmappe erstellen
      const workbook = XLSX.utils.book_new();
      
      // Arbeitsblatt aus Daten erstellen
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Spaltenbreiten anpassen
      const columnWidths = [
        { wch: 5 },    // ID
        { wch: 25 },   // Titel
        { wch: 40 },   // Beschreibung
        { wch: 12 },   // Kategorie
        { wch: 12 },   // Priorität
        { wch: 12 },   // Status
        { wch: 20 },   // Erstellt von
        { wch: 20 },   // Erstellt am
        { wch: 20 },   // Bearbeitet von
        { wch: 20 }    // Bearbeitet am
      ];
      worksheet['!cols'] = columnWidths;

      // Arbeitsblatt zur Arbeitsmappe hinzufügen
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Dienstübergaben');

      // Excel-Datei als ArrayBuffer erstellen
      const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

      // Dateiname mit Zeitstempel erstellen
      const now = new Date();
      const timestamp = now.toISOString().split('T')[0]; // YYYY-MM-DD
      const fileName = `Dienstübergabe_${timestamp}.xlsx`;

      // Datei speichern
      await saveFileToDirectory(CATEGORY, fileName, excelBuffer);
      
      console.log(`Dienstübergaben erfolgreich gespeichert: ${fileName}`);
    } catch (error) {
      console.error('Fehler beim Speichern der Übergaben:', error);
      throw error;
    }
  };

  const handleDirectorySelect = async () => {
    try {
      const dirName = await selectDirectory(CATEGORY);
      
      if (dirName) {
        setIsDirectorySelected(true);
        setMessage({ text: `Speicherort ausgewählt: ${dirName}`, type: 'success' });
        
        // Versuche, vorhandene Daten zu laden
        await loadUebergaben();
      }
    } catch (error) {
      console.error('Fehler bei der Verzeichnisauswahl:', error);
      setMessage({ text: `Fehler bei der Verzeichnisauswahl: ${error.message}`, type: 'error' });
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'offen': return '#f59e0b';
      case 'in_bearbeitung': return '#3b82f6';
      case 'erledigt': return '#10b981';
      case 'archiviert': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'offen': return 'Offen';
      case 'in_bearbeitung': return 'In Bearbeitung';
      case 'erledigt': return 'Erledigt';
      case 'archiviert': return 'Archiviert';
      default: return status;
    }
  };

  const getPrioritaetColor = (prioritaet) => {
    switch (prioritaet) {
      case 'niedrig': return '#10b981';
      case 'normal': return '#3b82f6';
      case 'hoch': return '#f59e0b';
      case 'kritisch': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unbekannt';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="dienstuebergabe-container">
      <div className="dienstuebergabe-header">
        <h1>📋 Dienstübergabe</h1>
        <div className="header-actions">
          {!isDirectorySelected ? (
            <button 
              className="btn-secondary"
              onClick={handleDirectorySelect}
            >
              <i className="fas fa-folder-open"></i> Speicherort auswählen
            </button>
          ) : (
            <button 
              className="btn-primary"
              onClick={() => setShowForm(true)}
            >
              <i className="fas fa-plus"></i> Neue Übergabe
            </button>
          )}
        </div>
      </div>

      {/* Nachrichten */}
      {message.text && (
        <div className={`message message-${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Kategorien-Tabs */}
      <div className="kategorien-tabs">
        <button 
          className={`tab ${activeTab === 'kurzfristig' ? 'active' : ''}`}
          onClick={() => setActiveTab('kurzfristig')}
        >
          <i className="fas fa-clock"></i> Kurzfristig
          <span className="badge">{uebergaben.kurzfristig.length}</span>
        </button>
        <button 
          className={`tab ${activeTab === 'mittelfristig' ? 'active' : ''}`}
          onClick={() => setActiveTab('mittelfristig')}
        >
          <i className="fas fa-calendar-week"></i> Mittelfristig
          <span className="badge">{uebergaben.mittelfristig.length}</span>
        </button>
        <button 
          className={`tab ${activeTab === 'langfristig' ? 'active' : ''}`}
          onClick={() => setActiveTab('langfristig')}
        >
          <i className="fas fa-calendar-alt"></i> Langfristig
          <span className="badge">{uebergaben.langfristig.length}</span>
        </button>
      </div>

      {/* Formular für neue Übergabe */}
      {showForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>Neue Übergabe erstellen</h3>
              <button 
                className="close-btn"
                onClick={() => setShowForm(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="uebergabe-form">
              <div className="form-group">
                <label>Titel *</label>
                <input
                  type="text"
                  value={newUebergabe.titel}
                  onChange={(e) => setNewUebergabe({...newUebergabe, titel: e.target.value})}
                  placeholder="Kurze Beschreibung der Übergabe"
                  required
                />
              </div>

              <div className="form-group">
                <label>Beschreibung *</label>
                <textarea
                  value={newUebergabe.beschreibung}
                  onChange={(e) => setNewUebergabe({...newUebergabe, beschreibung: e.target.value})}
                  placeholder="Detaillierte Beschreibung der Übergabe"
                  rows="4"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Kategorie</label>
                  <select
                    value={newUebergabe.kategorie}
                    onChange={(e) => setNewUebergabe({...newUebergabe, kategorie: e.target.value})}
                  >
                    <option value="kurzfristig">Kurzfristig</option>
                    <option value="mittelfristig">Mittelfristig</option>
                    <option value="langfristig">Langfristig</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Priorität</label>
                  <select
                    value={newUebergabe.prioritaet}
                    onChange={(e) => setNewUebergabe({...newUebergabe, prioritaet: e.target.value})}
                  >
                    <option value="niedrig">Niedrig</option>
                    <option value="normal">Normal</option>
                    <option value="hoch">Hoch</option>
                    <option value="kritisch">Kritisch</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" onClick={() => setShowForm(false)}>
                  Abbrechen
                </button>
                <button type="submit" className="btn-primary">
                  <i className="fas fa-save"></i> Erstellen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Übergaben-Liste */}
      <div className="uebergaben-liste">
        {loading ? (
          <div className="loading">Lade Übergaben...</div>
        ) : (
          uebergaben[activeTab].map((uebergabe) => (
            <div key={uebergabe.id} className="uebergabe-card">
              <div className="uebergabe-header">
                <div className="uebergabe-titel">
                  <h3>{uebergabe.titel}</h3>
                  <div className="uebergabe-meta">
                    <span 
                      className="prioritaet-badge"
                      style={{ backgroundColor: getPrioritaetColor(uebergabe.prioritaet) }}
                    >
                      {uebergabe.prioritaet.toUpperCase()}
                    </span>
                    <span 
                      className="status-badge"
                      style={{ backgroundColor: getStatusColor(uebergabe.status) }}
                    >
                      {getStatusText(uebergabe.status)}
                    </span>
                  </div>
                </div>
                <div className="uebergabe-actions">
                  <select
                    value={uebergabe.status}
                    onChange={(e) => handleStatusChange(uebergabe.id, e.target.value)}
                    className="status-select"
                  >
                    <option value="offen">Offen</option>
                    <option value="in_bearbeitung">In Bearbeitung</option>
                    <option value="erledigt">Erledigt</option>
                    <option value="archiviert">Archiviert</option>
                  </select>
                  <button 
                    className="delete-btn"
                    onClick={() => handleDelete(uebergabe.id)}
                    title="Löschen"
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>

              <div className="uebergabe-beschreibung">
                <p>{uebergabe.beschreibung}</p>
              </div>

              <div className="uebergabe-footer">
                <div className="uebergabe-info">
                  <div className="info-item">
                    <i className="fas fa-user"></i>
                    <span>Erstellt von: {uebergabe.erstelltVon}</span>
                  </div>
                  <div className="info-item">
                    <i className="fas fa-clock"></i>
                    <span>Erstellt am: {formatDate(uebergabe.erstelltAm)}</span>
                  </div>
                  {uebergabe.bearbeitetVon && uebergabe.bearbeitetVon !== uebergabe.erstelltVon && (
                    <div className="info-item">
                      <i className="fas fa-edit"></i>
                      <span>Bearbeitet von: {uebergabe.bearbeitetVon}</span>
                    </div>
                  )}
                  {uebergabe.bearbeitetAm && (
                    <div className="info-item">
                      <i className="fas fa-clock"></i>
                      <span>Bearbeitet am: {formatDate(uebergabe.bearbeitetAm)}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}

        {uebergaben[activeTab].length === 0 && !loading && (
          <div className="empty-state">
            <i className="fas fa-clipboard-list"></i>
            <h3>Keine Übergaben vorhanden</h3>
            <p>Erstellen Sie die erste Übergabe für die Kategorie "{activeTab}".</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Dienstuebergabe;
